/**
 * Adds a watermark to an image
 * @param {Blob} imageBlob - The image blob to watermark
 * @param {Object} options - Watermark options
 * @param {string} options.text - Watermark text (default: "PlayMaker Pics")
 * @param {string} options.color - Watermark color (default: "rgba(255, 255, 255, 0.5)")
 * @param {string} options.font - Watermark font (default: "24px Arial")
 * @param {string} options.fontSize - Font size (default: "24px")
 * @param {number} options.opacity - Global opacity 0-1 (default: uses color alpha)
 * @param {number} options.position - Position (1-9, like numpad, default: 9 for bottom-right)
 * @param {boolean} options.diagonal - Whether to draw text diagonally (default: false)
 * @param {boolean} options.repeating - Whether to create a repeating pattern (default: false)
 * @returns {Promise<Blob>} The watermarked image as a blob
 */
export const addWatermark = async (imageBlob, options = {}) => {
  return new Promise((resolve, reject) => {
    const {
      text = "PlayMaker Pics",
      color = "rgba(255, 255, 255, 0.5)",
      font = "24px Arial",
      fontSize = "24px",
      opacity = null, // If null, use color alpha
      position = 9,
      diagonal = false,
      repeating = false
    } = options;
    
    // Create image from blob
    const img = new Image();
    img.onload = () => {
      // Create canvas
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      
      // Draw original image
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      
      // Add watermark text
      ctx.font = fontSize ? `bold ${fontSize} Arial` : font;
      ctx.fillStyle = color;
      
      // Set global opacity if specified
      if (opacity !== null) {
        ctx.globalAlpha = opacity;
      }
      
      // Measure text
      const textWidth = ctx.measureText(text).width;
      const textHeight = parseInt(font, 10); // Rough height estimate
      
      // If repeating pattern is requested
      if (repeating) {
        // Save original global alpha
        const originalAlpha = ctx.globalAlpha;
        
        // Set transparency for repeating pattern - make it more visible
        ctx.globalAlpha = 0.6; // Increased from 0.4 for much more visibility
        
        // Space between watermarks
        const spacingX = textWidth * 2; // Wider spacing to accommodate larger text
        const spacingY = textHeight * 2; // Wider spacing to accommodate larger text
        
        // Use a larger font for watermark
        const originalFont = ctx.font;
        ctx.font = "bold 72px Arial"; // MUCH larger font size
        
        // Remeasure text with new font size
        const largeTextWidth = ctx.measureText(text).width;
        const largeTextHeight = 72; // Approximate height based on font size
        
        // Draw repeated watermark text in a grid pattern
        for (let y = largeTextHeight; y < canvas.height; y += spacingY + largeTextHeight) {
          for (let x = 0; x < canvas.width; x += spacingX + largeTextWidth/2) {
            ctx.fillText(text, x, y);
          }
        }
        
        // Draw diagonal watermarks for better coverage
        ctx.save();
        
        // Move to center of canvas and rotate
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate(-Math.PI / 4); // -45 degrees in radians
        
        // Draw diagonal grid of watermarks
        const diagonal_length = Math.sqrt(canvas.width * canvas.width + canvas.height * canvas.height);
        for (let y = -diagonal_length/2; y < diagonal_length/2; y += spacingY + largeTextHeight) {
          for (let x = -diagonal_length/2; x < diagonal_length/2; x += spacingX + largeTextWidth/2) {
            ctx.fillText(text, x, y);
          }
        }
        
        // Restore context
        ctx.restore();
        
        // Restore original alpha and font
        ctx.globalAlpha = originalAlpha;
        ctx.font = originalFont;
      }
      
      // If diagonal watermark is requested
      if (diagonal) {
        // Save context state
        ctx.save();
        
        // Set transparency for diagonal text
        ctx.globalAlpha = 0.7; // Increased from 0.5 for much more visibility
        
        // Use a larger font for diagonal watermark
        ctx.font = "bold 120px Arial"; // MUCH larger font size for single diagonal watermark
        
        // Remeasure text with new font size
        const largeTextWidth = ctx.measureText(text).width;
        
        // Move to center of canvas
        ctx.translate(canvas.width / 2, canvas.height / 2);
        
        // Rotate canvas
        ctx.rotate(-Math.PI / 4); // -45 degrees in radians
        
        // Draw text centered
        ctx.fillText(text, -largeTextWidth / 2, 0);
        
        // Restore context
        ctx.restore();
      }
      // Standard positioned watermark
      else {
        // Calculate position based on numpad layout (1-9)
        let x, y;
        
        // Horizontal position (1,4,7=left; 2,5,8=center; 3,6,9=right)
        if (position % 3 === 1) {
          x = 20; // Left
        } else if (position % 3 === 2) {
          x = (canvas.width - textWidth) / 2; // Center
        } else {
          x = canvas.width - textWidth - 20; // Right
        }
        
        // Vertical position (1,2,3=top; 4,5,6=middle; 7,8,9=bottom)
        if (position <= 3) {
          y = textHeight + 20; // Top
        } else if (position <= 6) {
          y = canvas.height / 2; // Middle
        } else {
          y = canvas.height - 20; // Bottom
        }
        
        // Draw text
        ctx.fillText(text, x, y);
      }
      
      // Convert back to blob
      canvas.toBlob(blob => {
        resolve(blob);
      }, imageBlob.type);
    };
    
    img.onerror = () => {
      reject(new Error('Failed to load image for watermarking'));
    };
    
    img.src = URL.createObjectURL(imageBlob);
  });
};

/**
 * Adds a logo watermark to an image
 * @param {Blob} imageBlob - The image blob to watermark
 * @param {string} logoUrl - URL of the logo to use as watermark
 * @param {Object} options - Watermark options
 * @param {number} options.size - Size percentage of the shorter dimension (default: 15)
 * @param {number} options.opacity - Opacity from 0-1 (default: 0.5)
 * @param {number} options.position - Position (1-9, like numpad, default: 9 for bottom-right)
 * @param {number} options.margin - Margin from edge in pixels (default: 20)
 * @returns {Promise<Blob>} The watermarked image as a blob
 */
export const addLogoWatermark = async (imageBlob, logoUrl, options = {}) => {
  return new Promise((resolve, reject) => {
    const {
      size = 15, // % of shorter dimension
      opacity = 0.5,
      position = 9, // Bottom right
      margin = 20 // pixels from edge
    } = options;
    
    // Create image from blob
    const img = new Image();
    const logo = new Image();
    
    // Load both images before proceeding
    Promise.all([
      new Promise(resolve => {
        img.onload = resolve;
        img.onerror = () => reject(new Error('Failed to load main image'));
        img.src = URL.createObjectURL(imageBlob);
      }),
      new Promise(resolve => {
        logo.onload = resolve;
        logo.onerror = () => reject(new Error('Failed to load logo image'));
        logo.src = logoUrl;
      })
    ]).then(() => {
      // Create canvas
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      
      // Draw original image
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      
      // Calculate logo size to be a percentage of the shorter dimension
      const shorterDimension = Math.min(canvas.width, canvas.height);
      const logoSize = shorterDimension * (size / 100);
      const ratio = logo.width / logo.height;
      
      // Keep aspect ratio
      let logoWidth, logoHeight;
      if (ratio > 1) {
        // Wider than tall
        logoWidth = logoSize;
        logoHeight = logoSize / ratio;
      } else {
        // Taller than wide or square
        logoHeight = logoSize;
        logoWidth = logoSize * ratio;
      }
      
      // Calculate position based on numpad layout (1-9)
      let x, y;
      
      // Horizontal position (1,4,7=left; 2,5,8=center; 3,6,9=right)
      if (position % 3 === 1) {
        x = margin; // Left
      } else if (position % 3 === 2) {
        x = (canvas.width - logoWidth) / 2; // Center
      } else {
        x = canvas.width - logoWidth - margin; // Right
      }
      
      // Vertical position (1,2,3=top; 4,5,6=middle; 7,8,9=bottom)
      if (position <= 3) {
        y = margin; // Top
      } else if (position <= 6) {
        y = (canvas.height - logoHeight) / 2; // Middle
      } else {
        y = canvas.height - logoHeight - margin; // Bottom
      }
      
      // Set opacity
      ctx.globalAlpha = opacity;
      
      // Draw logo
      ctx.drawImage(logo, x, y, logoWidth, logoHeight);
      
      // Reset opacity
      ctx.globalAlpha = 1.0;
      
      // Convert back to blob
      canvas.toBlob(blob => {
        resolve(blob);
      }, imageBlob.type);
    }).catch(err => {
      reject(err);
    });
  });
}; 